import { getS, setS } from "../../core/state";
import { getListData, getSectionMeta } from "../../core/stateUtils";
import { withUploadedImageCacheBust } from "../../core/imageMedia";
import ProductCard from "../widgets/ProductCard";
import { RefreshCw, LayoutGrid, List } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const normalizeUrls = (value) => {
  if (Array.isArray(value)) return value.map((v) => String(v || "").trim()).filter(Boolean);
  if (typeof value === "string" && value.trim()) {
    const raw = value.trim();
    if (raw.startsWith("[") && raw.endsWith("]")) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.map((v) => String(v || "").trim()).filter(Boolean);
      } catch (e) {
        // fall through
      }
    }
    if (raw.includes(",")) return raw.split(",").map((v) => v.trim()).filter(Boolean);
    return [raw];
  }
  return [];
};

/* =============================================
   GRID SECTION — JSON-driven list/card renderer
   Supports: table view + card/grid view
   ============================================= */
export default function GridSection({ section, actions }) {
  const s = getS();
  const navigate = useNavigate();
  const data = getListData(s, section);
  const meta = getSectionMeta(s, section);
  const [viewMode, setViewMode] = useState(section.view === "grid" ? "grid" : "table");
  const activeFilters = Object.entries(s.filters || {}).filter(([, value]) => {
    if (value === undefined || value === null) return false;
    const v = String(value).trim();
    if (!v) return false;
    return v.toLowerCase() !== "all";
  });
  const activeFilterCount = activeFilters.length;
  const showFilterHint = String(section.id || "").toLowerCase() === "products" && activeFilterCount > 0;
  const showCatalogLoading = String(section.id || "").toLowerCase() === "products" && s.isCatalogLoading === true;

  const handleAction = (action) => {
    switch (action.type) {
      case "refresh":
        window.location.reload();
        break;
      case "add":
        setS({
          form: {
            sku: "",
            name: "",
            productType: "saree",
            spec: {},
            zipImport: "",
            multiImageImport: "",
            images: "",
            availableColors: "",
            colorImageMap: "",
            variantGalleryMap: "",
            productJson: "",
            uploadedAt: "",
          },
          editId: null,
          errors: {},
          lastActionMarker: Date.now(),
        });
        break;
      default:
        break;
    }
  };

  const handleRowClick = (row) => {
    if (section.rowClick?.type === "navigate" && section.rowClick.to) {
      const routeId = String(row?.id ?? row?.sku ?? "").trim();
      const to = section.rowClick.to.replace(":id", routeId);
      navigate(to, { state: { detailItem: row } });
      return;
    }
    // Ensure images is an array for the file upload component
    const formData = { ...row };
    formData.images = normalizeUrls(formData.images);
    setS({ form: formData, editId: row.id, lastActionMarker: Date.now() });
  };

  const isCard = viewMode === "grid";

  return (
    <div className={`lfx-card ${section.className || ""}`}>
      {/* HEADER */}
      <div className="lfx-card-header">
        <span className="lfx-card-title">{section.title}</span>

        <div className="lfx-card-header-actions">
          {/* count badge */}
          {meta.totalRecords !== undefined && (
            <span className="lfx-badge">{meta.totalRecords} items</span>
          )}
          {showFilterHint ? (
            <span className="lfx-badge">{activeFilterCount} filters active</span>
          ) : null}

          {/* view toggle */}
          <div className="lfx-view-toggle">
            <button
              className={`lfx-view-btn ${viewMode === "grid" ? "active" : ""}`}
              onClick={() => setViewMode("grid")}
              title="Card View"
            >
              <LayoutGrid size={15} />
            </button>
            <button
              className={`lfx-view-btn ${viewMode === "table" ? "active" : ""}`}
              onClick={() => setViewMode("table")}
              title="Table View"
            >
              <List size={15} />
            </button>
          </div>

          {/* header actions from JSON */}
          {section.headerActions?.map((action, i) => (
            <button
              key={i}
              className="lfx-btn primary sm"
              onClick={() => handleAction(action)}
            >
              {action.label || <RefreshCw size={14} />}
            </button>
          ))}
        </div>
      </div>

      {/* BODY */}
      <div className="lfx-card-body">
        {showCatalogLoading ? (
          <div className="lfx-loading-state" role="status" aria-live="polite">
            <span className="lfx-spinner" aria-hidden />
            <span>Loading products...</span>
          </div>
        ) : data.length === 0 ? (
          <div className="lfx-empty-state">
            <span>
              {showFilterHint
                ? `No products match current filters (${activeFilterCount} active). Click Clear Filters/Clear all.`
                : "No products available."}
            </span>
          </div>
        ) : isCard ? (
          <div className="lfx-product-grid">
            {data.map((row, i) => (
              <ProductCard
                key={`${row.id || i}-${row.updatedAt || row.uploadedAt || ""}`}
                item={row}
                template={section.cardTemplate}
                onClick={() => handleRowClick(row)}
              />
            ))}
          </div>
        ) : (
          <TableView
            columns={section.columns}
            data={data}
            onRowClick={handleRowClick}
            activeId={s.editId}
          />
        )}
      </div>

      {/* PAGINATION */}
      {meta.totalRecords > 0 && (
        <Pagination meta={meta} sectionId={section.id} />
      )}
    </div>
  );
}

const resolveImageSrc = (row, field) => {
  const raw = row?.[field];

  const direct = normalizeUrls(raw);
  if (direct.length) return withUploadedImageCacheBust(direct[0], row);

  const fallbacks = [row?.images2, row?.images, row?.image, row?.thumbnail, row?.thumbnailUrl, row?.mainImage];
  for (const val of fallbacks) {
    const list = normalizeUrls(val);
    if (list.length) return withUploadedImageCacheBust(list[0], row);
  }
  return "";
};

const formatCellValue = (row, col) => {
  const value = row?.[col.field];
  if (col.field === "availableColors") {
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "string" && value.trim()) return value;
  }
  return value ?? "—";
};

/* ===== TABLE VIEW ===== */
function TableView({ columns, data, onRowClick, activeId }) {
  return (
    <div className="lfx-table-wrap">
      <table className="lfx-table">
        <thead>
          <tr>
            {columns?.map((col, i) => (
              <th key={i}>{col.label || col.field}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, ri) => (
            <tr
              key={row.id || ri}
              className={`lfx-table-row ${row.id === activeId ? "active" : ""}`}
              onClick={() => onRowClick(row)}
            >
              {columns?.map((col, ci) => (
                <td key={ci}>
                  {col.type === "image" ? (
                    (() => {
                      const src = resolveImageSrc(row, col.field);
                      return src ? (
                        <img src={src} alt={row?.name || "product"} className="lfx-table-img" />
                      ) : (
                        "—"
                      );
                    })()
                  ) : (
                    formatCellValue(row, col)
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ===== PAGINATION ===== */
function Pagination({ meta, sectionId }) {
  const { totalRecords = 0, page = 1, pageSize = 12 } = meta;
  const totalPages = Math.ceil(totalRecords / pageSize);
  if (totalPages <= 1) return null;

  return (
    <div className="lfx-pagination">
      <span className="lfx-page-info">
        Page {page} of {totalPages} ({totalRecords} items)
      </span>
      <div className="lfx-page-btns">
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
          <button
            key={p}
            className={`lfx-page-btn ${p === page ? "active" : ""}`}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}
